import type { InsurerData, Locale, Package } from "./insurance-types"
import {
  calculateCoverageReimbursement,
  getCoverage,
  isEstimatedCoverage,
} from "./insurers"
import { PAGE_COVERAGE_SERVICE_IDS, isRealPackageId } from "./insurer-pages"

/**
 * What a per-insurer page says each supplementary package pays back — derived
 * from the same CMS coverage rows the checker computes with, so the cards
 * above the fold and the cards below it can never disagree, and a Payload edit
 * is the only thing needed to correct a figure.
 */
export interface PackageCoverageRow {
  serviceId: string
  /** Localized service label from the CMS ("Core MRI (CHF 1'990)"). */
  label: string
  /** Reimbursement in CHF for that check-up; null when it isn't covered. */
  amount: number | null
  /**
   * The insurer hasn't committed to this figure — `likely` / `itemized` rows
   * (Groupe Mutuel today). Rendered as "up to X" rather than a flat number,
   * matching how the checker's result panel treats them.
   */
  estimated: boolean
}

export interface InsurerPagePackage {
  id: string
  /** Package name as the CMS spells it ("Pulse Top"). */
  name: string
  rows: PackageCoverageRow[]
  /** No check-up in the list gets a franc back under this package. */
  coversNothing: boolean
}

function serviceLabel(
  service: { name_de: string; name_en: string; name_fr: string },
  locale: Locale
): string {
  if (locale === "de") return service.name_de
  if (locale === "fr") return service.name_fr
  return service.name_en
}

/**
 * The packages a per-insurer page renders, in CMS order, each with its
 * reimbursement per check-up. Excludes the `*-other` free-text option.
 * Returns `[]` when the insurer isn't in the data (CMS fetch failed, or the
 * insurer was removed from Payload) — the caller drops the section.
 */
export function insurerPagePackages(
  data: InsurerData | null,
  insurerId: string,
  locale: Locale
): InsurerPagePackage[] {
  if (!data) return []
  const insurer = data.insurers.find((ins) => ins.id === insurerId)
  if (!insurer) return []

  return insurer.packages
    .filter((pkg: Package) => isRealPackageId(pkg.id))
    .map((pkg: Package) => {
      const rows = PAGE_COVERAGE_SERVICE_IDS.flatMap((serviceId) => {
        const service = data.services.find((s) => s.id === serviceId)
        if (!service) return []
        const coverage = getCoverage(data, insurerId, pkg.id, serviceId)
        const amount = coverage
          ? calculateCoverageReimbursement(coverage, serviceId, service.price)
          : null
        return [
          {
            serviceId,
            label: serviceLabel(service, locale),
            // A zero reimbursement is "not covered" to a reader, so it reads
            // as one rather than as "CHF 0".
            amount: amount && amount > 0 ? amount : null,
            estimated: isEstimatedCoverage(coverage),
          },
        ]
      })
      return {
        id: pkg.id,
        name: pkg.name,
        rows,
        coversNothing: rows.every((row) => row.amount === null),
      }
    })
}
